import { ref } from 'vue'

import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { productToProductCard } from '@backmarket/nuxt-layer-recommendation/helpers.ts'
import type { Product } from '@backmarket/nuxt-layer-recommendation/models/product'
import { useAddToCartPopover } from '@backmarket/nuxt-layer-recommendation/useAddToCartPopover'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'

export type AddToCartStatus = 'idle' | 'pending' | 'success' | 'error'

// Mirrors nuxt-layer-recommendation's `useAddToCart` + `AddToCartButton`'s
// success/error handling (POST the listing, refresh the user so the header's
// cart badge updates, then trigger the same express-ATC popover) — that
// composable and component aren't exported from the package, so this
// reimplements just the part we need rather than the full carousel/PDP
// machinery (comparison, flash-sale promo auto-apply, etc.).
export function useLiveShoppingAddToCart() {
  const $httpFetch = useHttpFetch()
  const { fetchUser } = useUserStore()
  const { show: showAddToCartPopover, showError: showAddToCartError } =
    useAddToCartPopover()

  const status = ref<AddToCartStatus>('idle')

  async function addToCart(product: Product) {
    status.value = 'pending'

    try {
      await $httpFetch('/bm/cart/add_product', {
        method: 'POST',
        body: { listing_id: Number(product.listingId) },
      })
      await fetchUser()
      status.value = 'success'
      await showAddToCartPopover(productToProductCard(product))
    } catch {
      status.value = 'error'
      showAddToCartError()
    }
  }

  return { addToCart, status }
}
